<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-3">
        <a-switch
          size="small"
          v-model:checked="enabled"
          :loading="saving"
        />
        <span class="text_color text-xl font-semibold">Enable WhatsApp</span>
      </div>
      <span class="text-sm font-normal secondary_color ml-10">
        Assign a WhatsApp number for support conversations
      </span>
    </div>

    <div v-if="loading" class="flex justify-center py-10">
      <CustomLoader />
    </div>

    <div v-else-if="enabled" class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium">Account to use for support</span>

        <a-select
          v-model:value="selectedPhoneNumber"
          placeholder="Select phone number"
          :options="phoneNumberOptions"
          :filter-option="filterOption"
          class="w-2/3"
          show-search
          allow-clear
        />
      </div>

      <div class="flex justify-start">
        <a-button
          class="primary_medium"
          :loading="saving"
          :disabled="!selectedPhoneNumber"
          @click="handleSave"
        >
          Save Changes
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { message } from "ant-design-vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import ConversationService from "@/src/api/ConversationService.js";
import UniboxService from "@/src/api/UniboxService.js";
import { normalizePhoneNumber } from "@/src/utils/phoneNumberUtils.js";

const loading = ref(false);
const saving = ref(false);
const initialized = ref(false);
const enabled = ref(false);
const selectedPhoneNumber = ref(null);
const allPhoneNumbers = ref([]);
const uniboxAssignedNumbers = ref([]);

const phoneNumberOptions = computed(() => {
  return allPhoneNumbers.value
    .filter((config) => !uniboxAssignedNumbers.value.includes(normalizePhoneNumber(config.phone_number)))
    .map((config) => ({
      label: config.phone_number,
      value: config.phone_number,
    }));
});

const filterOption = (input, option) => {
  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
};

watch(enabled, async (newVal, oldVal) => {
  if (!initialized.value) return;
  if (oldVal === true && newVal === false) {
    try {
      saving.value = true;
      await ConversationService.updateSupportWhatsAppSettings({
        enabled: false,
        default_phone_number: null,
        default_phone_number_id: null,
        default_vendor_type: null,
        whatsapp_config_id: null,
      });
      selectedPhoneNumber.value = null;
      message.success("WhatsApp support disabled");
    } catch (error) {
      console.error("Error disabling WhatsApp support:", error);
      message.error("Failed to disable WhatsApp support");
      enabled.value = true;
    } finally {
      saving.value = false;
    }
  }
});

const fetchAllPhoneNumbers = async () => {
  try {
    const response = await UniboxService.fetchWhatsAppConfigs(true);
    if (response && response.success && response.configs) {
      allPhoneNumbers.value = response.configs.map((config) => ({
        id: config.id,
        phone_number: normalizePhoneNumber(config.phone_number),
        phone_number_id: config.phone_number_id,
        vendor_type: config.vendor_type,
      }));
    }
  } catch (error) {
    console.error("Error fetching all phone numbers:", error);
  }
};

const fetchUniboxAssignedNumbers = async () => {
  try {
    const response = await UniboxService.fetchUniboxWhatsAppNumbers();
    if (response && response.success && response.phone_numbers) {
      uniboxAssignedNumbers.value = response.phone_numbers.map(normalizePhoneNumber);
    }
  } catch (error) {
    console.error("Error fetching unibox WhatsApp numbers:", error);
  }
};

const fetchSupportSettings = async () => {
  try {
    const response = await ConversationService.getSupportWhatsAppSettings();
    if (response && response.success && response.data) {
      enabled.value = response.data.enabled || false;
      if (response.data.default_phone_number) {
        selectedPhoneNumber.value = normalizePhoneNumber(response.data.default_phone_number);
      }
    }
  } catch (error) {
    console.error("Error fetching support WhatsApp settings:", error);
  }
};

const handleSave = async () => {
  try {
    saving.value = true;

    const selectedConfig = allPhoneNumbers.value.find(
      (c) => c.phone_number === selectedPhoneNumber.value
    );

    const response = await ConversationService.updateSupportWhatsAppSettings({
      enabled: enabled.value,
      default_phone_number: selectedPhoneNumber.value,
      default_phone_number_id: selectedConfig?.phone_number_id || null,
      default_vendor_type: selectedConfig?.vendor_type || null,
      whatsapp_config_id: selectedConfig?.id || null,
    });

    if (response && response.success) {
      message.success("Settings saved successfully");
    } else {
      message.error("Failed to save settings");
    }
  } catch (error) {
    console.error("Error saving support WhatsApp settings:", error);
    message.error("An error occurred while saving settings");
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  loading.value = true;
  try {
    await Promise.all([
      fetchAllPhoneNumbers(),
      fetchUniboxAssignedNumbers(),
      fetchSupportSettings(),
    ]);
  } finally {
    loading.value = false;
    initialized.value = true;
  }
});
</script>
